import React, {useEffect, useMemo, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import {deepEqual} from "../../../../core/services/utils/utils";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Divider from "@mui/material/Divider";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import {CircularProgress, Tooltip} from "@mui/material";
import {ReactComponent as PassedIcon} from "../../../../assets/images/validations/passed.svg";
import {ReactComponent as FailedIcon} from "../../../../assets/images/validations/failed.svg";
import {MuiRFF, Yup} from '../../form';
import DateUtils from "../../../../core/services/date-utils";
import {InfoOutlined} from "@mui/icons-material";
import {CheckboxForm} from "../../base/checkbox/checkbox";

const formNames = {
    code: 'code',
    budget: 'budget',
    amountPerRedeem: 'amountPerRedeem',
    expiryDate: 'expiryDate',
    multipleUse: 'multipleUse',
    applyAmount: 'applyAmount',
}

const schema = Yup.object().shape({
    [formNames.code]: Yup.string()
        .trim()
        .test(formNames.code, 'Code should be less than 16 characters. ',
            async (val) => {
                let _val = val?.trim();
                if (!_val || _val?.length === 0)
                    return false;
                return val.length <= 16;
            }
        ).nullable().required(ValidateMessages.required),
    [formNames.budget]: Yup.number().nullable().typeError(ValidateMessages.incorrectType('Number'))
        .min(0, ValidateMessages.greaterThan('0')).required(ValidateMessages.required),
    [formNames.amountPerRedeem]: Yup.number().when(formNames.budget, {
        is: value => !!value?.toString() && !isNaN(parseFloat(value)),
        then: Yup.number().nullable().typeError(ValidateMessages.incorrectType('Number'))
            .max(Yup.ref(formNames.budget), ValidateMessages.lessThan('Budget')),
        otherwise: Yup.number().nullable().typeError(ValidateMessages.incorrectType('Number')).required(ValidateMessages.required)
    }),
    [formNames.multipleUse]: Yup.boolean().nullable().default(false),
    [formNames.applyAmount]: Yup.number().when(formNames.budget, {
        is: value => !!value?.toString() && !isNaN(parseFloat(value)),
        then: Yup.number().nullable().typeError(ValidateMessages.incorrectType('Number'))
            .max(Yup.ref(formNames.budget), ValidateMessages.lessThan('Budget')),
        otherwise: Yup.number().nullable().typeError(ValidateMessages.incorrectType('Number')).required(ValidateMessages.required)
    }),
    [formNames.expiryDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);


/**
 * Different statuses of part number text field in the quick order entities
 */
const PromotionCodeFieldStatus = {
    UnTouched: 'unTouched',
    Valid: 'valid',
    InValid: 'inValid',
    Loading: 'loading',
}

const UpsertPromotionDialog = ({open, setOpen, selectedPromotion, onSuccess}) => {
    const [loading, setLoading] = useState(false);
    const [codeFieldStatus, setCodeFieldStatus] = useState(PromotionCodeFieldStatus.UnTouched);
    const isMounted = useIsMounted();

    /**
     * Memo version of status Icon next to text field
     * @type {JSX.Element}
     */
    const statusIcon = useMemo(() => {
        switch (codeFieldStatus) {
            case PromotionCodeFieldStatus.Valid:
                return <div className={'mb-3'}>
                    <PassedIcon/>
                </div>;
            case PromotionCodeFieldStatus.InValid:
                return <div className={'error-icon'}>
                    <FailedIcon/>
                </div>;
            case PromotionCodeFieldStatus.Loading:
                return <CircularProgress size={24}/>;
            default:
                return <div/>
        }
    }, [codeFieldStatus])

    const errorMessage = useMemo(() => {
        switch (codeFieldStatus) {
            case PromotionCodeFieldStatus.InValid:
                return "Duplicated Code"
            case PromotionCodeFieldStatus.Valid:
                return ""
            default:
                return '';
        }
    }, [codeFieldStatus])

    /**
     * Memo version of indicator of dialog action
     * @type {boolean}
     */
    const forUpdate = useMemo(() => {
        return selectedPromotion !== null;
    }, [selectedPromotion])

    /**
     * Memo version of initial values of the form
     * @type {unknown}
     */
    const initialValues = useMemo(() => {
        if (selectedPromotion !== null) {
            return {
                [formNames.code]: selectedPromotion?.code,
                [formNames.budget]: selectedPromotion?.amount,
                [formNames.amountPerRedeem]: selectedPromotion?.userAmount,
                [formNames.expiryDate]: selectedPromotion?.expiryDate ? DateUtils.serverToUTC(selectedPromotion?.expiryDate) : null,
                [formNames.multipleUse]: selectedPromotion?.multipleUse ?? false,
                [formNames.applyAmount]: selectedPromotion?.applyAmount,
            }
        }
        return {}
    }, [selectedPromotion])

    /**
     * Resets the code field status on each open
     */
    useEffect(() => {
        setCodeFieldStatus(PromotionCodeFieldStatus.UnTouched);
    }, [open])

    /**
     * Validates the code field
     * @param code
     * @returns {Promise<void>}
     */
    const validateCode = async (code) => {
        if (selectedPromotion && code?.trim() === selectedPromotion?.code) {
            setCodeFieldStatus(PromotionCodeFieldStatus.Valid);
            return;
        }
        if (!code?.trim()?.length) {
            setCodeFieldStatus(PromotionCodeFieldStatus.UnTouched);
            return;
        }
        setCodeFieldStatus(PromotionCodeFieldStatus.Loading);
        const response = await Api.validatePromoCode(code?.trim());
        if (!isMounted())
            return;
        if (response.data) {
            setCodeFieldStatus(PromotionCodeFieldStatus.Valid);
            return;
        }
        setCodeFieldStatus(PromotionCodeFieldStatus.InValid);
    }

    /**
     * Based on [forUpdate] indicator either creates or updates the budget
     * @param values
     * @return {Promise<void>}
     */
    const submit = async (values) => {
        setLoading(true);
        if (forUpdate) {
            const forApi = {
                id: selectedPromotion?.id,
                amount: parseFloat(values[formNames.budget]),
                code: values[formNames.code],
                expiryDate: values[formNames.expiryDate] ? DateUtils.UTCToServer(values[formNames.expiryDate]).format(DateUtils.withoutOffsetFormat) : null,
                userAmount: parseFloat(values[formNames.amountPerRedeem]),
                multipleUse: values[formNames.multipleUse],
                applyAmount: values[formNames.applyAmount] ? parseFloat(values[formNames.applyAmount]) : parseFloat(values[formNames.amountPerRedeem]),
            }
            const response = await Api.updateBudget(forApi);
            if (!isMounted())
                return;
            setLoading(false);
            if (response?.resultFlag)
                onSuccess(response?.data, false)
            return;
        }
        const forApi = {
            amount: parseFloat(values[formNames.budget]),
            code: values[formNames.code].trim(),
            expiryDate: values[formNames.expiryDate] ? DateUtils.UTCToServer(values[formNames.expiryDate]).format(DateUtils.withoutOffsetFormat) : null,
            userAmount: parseFloat(values[formNames.amountPerRedeem]),
            multipleUse: values[formNames.multipleUse],
            applyAmount: values[formNames.applyAmount] ? parseFloat(values[formNames.applyAmount]) : parseFloat(values[formNames.amountPerRedeem]),
        }
        const response = await Api.createBudget(forApi);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag)
            onSuccess(response?.data, true)
    }

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            className={'upsert-promo-dialog'}
        >
            <Row className='no-gutters justify-content-center align-items-start w-100'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                        {selectedPromotion !== null ? "Edit Promotion" : "Add a New Promotion"}
                    </p>
                </Row>
                <Form
                    className='w-100'
                    onSubmit={submit}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values, initialValues}) => {
                        const notChange = deepEqual(values, initialValues);
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className='no-gutters justify-content-around w-100 mx-4'>
                                    <Col xs={12} className={'d-flex align-items-center justify-content-start code-input-container'}>
                                        <div className={'pl-1 mr-1 mb-3 w-100'}>
                                            <OutlinedInputForm
                                                label={"Code"}
                                                required={required[formNames.code]}
                                                name={formNames.code}
                                                onBlur={(e) => validateCode(e.target.value)}
                                                helperText={errorMessage}
                                            />
                                        </div>
                                        {statusIcon}
                                    </Col>
                                    <Col xs={12} className={'mb-3 px-1'}>
                                        <OutlinedInputForm
                                            label={"Budget"}
                                            required={required[formNames.budget]}
                                            name={formNames.budget}
                                            type={'number'}
                                            inputProps={{
                                                min: 0,
                                            }}
                                        />
                                    </Col>
                                    <Col xs={12} className={'mb-3 px-1'}>
                                        <OutlinedInputForm
                                            label={"Amount per Redeem"}
                                            required={required[formNames.amountPerRedeem]}
                                            name={formNames.amountPerRedeem}
                                            type={'number'}
                                            inputProps={{
                                                min: 0,
                                                max: values[formNames.budget] ?? undefined,
                                            }}
                                        />
                                    </Col>
                                    <Col xs={12} className={'mb-3 px-1'}>
                                        <MuiRFF.DatePicker
                                            name={formNames.expiryDate}
                                            label={"Expiry Date (optional)"}
                                            required={required[formNames.expiryDate]}
                                            disablePast
                                        />
                                    </Col>
                                    <Col xs={12} className={'mb-3 px-1'}>
                                        <OutlinedInputForm
                                            label={"Apply Amount"}
                                            required={required[formNames.applyAmount]}
                                            name={formNames.applyAmount}
                                            type={'number'}
                                            inputProps={{
                                                min: 0,
                                                max: values[formNames.budget] ?? undefined,
                                            }}
                                            startAdornment={
                                                <Tooltip title={'The amount that will be applied to orders when this promotion is used. If not set, The redeem amount will be used.'}>
                                                    <InfoOutlined color={'secondary'} className={'mr-2'}/>
                                                </Tooltip>
                                            }
                                        />
                                    </Col>
                                    <Col xs={12} className={'mb-3 px-1'}>
                                        <CheckboxForm
                                            name={'multipleUse'}
                                            required={required.multipleUse}
                                            data={{
                                                value: values.multipleUse,
                                                label:
                                                    <div className={'d-flex align-items-center'}>
                                                        <p className={'text-sm mr-1'}>Can redeem multiple times</p>
                                                        <Tooltip title={'If checked, the promotion can be used multiple times by the same user.'}>
                                                            <InfoOutlined color={'secondary'}/>
                                                        </Tooltip>
                                                    </div>
                                            }}
                                        />
                                    </Col>
                                </Row>
                                <div className='my-2 w-100'/>
                                <Divider className='divider'/>
                                <Row className='no-gutters justify-content-between align-items-start w-100'>
                                    <Row className='no-gutters justify-content-between align-items-start w-100'>
                                        <Button
                                            disabled={!open || loading}
                                            variant={'dialog'}
                                            color={'primary'}
                                            onClick={() => setOpen(false)}
                                            borderRadius={'0 8px 0 8px'}
                                        >
                                            {loading ? 'loading...' : 'Close'}
                                        </Button>
                                        <Button
                                            disabled={!open || loading || notChange || codeFieldStatus === PromotionCodeFieldStatus.Loading || codeFieldStatus === PromotionCodeFieldStatus.InValid}
                                            variant={'dialog'}
                                            color={'primary'}
                                            type={'submit'}
                                            borderRadius={'8px 0 8px 0'}
                                        >
                                            {
                                                forUpdate
                                                    ? loading
                                                        ? 'Updating...'
                                                        : 'Edit'
                                                    : loading
                                                        ? 'Adding...'
                                                        : 'Add'
                                            }
                                        </Button>
                                    </Row>
                                </Row>
                            </Row>
                        );
                    }}
                />
            </Row>
        </Dialog>
    );
}

export default UpsertPromotionDialog;
